import React, { useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

interface ShareCardProps {
  open: boolean
  onClose: () => void
  score: number
  surah: number
  ayah: number
  surahName: string
  streak: number
  findings: number
}

const ShareCard: React.FC<ShareCardProps> = ({ open, onClose, score, surah, ayah, surahName, streak, findings }) => {
  const cardRef = useRef<HTMLDivElement>(null)

  const scoreColor = score >= 80 ? '#10b981' : score >= 60 ? '#eab308' : '#ef4444'
  const scoreLabel = score >= 80 ? 'Excellent!' : score >= 60 ? 'Good effort!' : 'Keep practicing!'

  const handleCopy = useCallback(async () => {
    const text = `I scored ${Math.round(score)}/100 on ${surahName} (${surah}:${ayah}) on Tajweed Tutor! ${score >= 80 ? '🎉' : '💪'}`
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      // Fallback
    }
  }, [score, surah, ayah, surahName])

  const handleDownload = useCallback(async () => {
    const card = cardRef.current
    if (!card) return

    // Use canvas to render the card
    const canvas = document.createElement('canvas')
    const scale = 2
    canvas.width = 400 * scale
    canvas.height = 280 * scale
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.scale(scale, scale)

    // Background
    const gradient = ctx.createLinearGradient(0, 0, 400, 280)
    gradient.addColorStop(0, '#064e3b')
    gradient.addColorStop(1, '#0f172a')
    ctx.fillStyle = gradient
    ctx.beginPath()
    ctx.roundRect(0, 0, 400, 280, 16)
    ctx.fill()

    // Score circle
    ctx.beginPath()
    ctx.arc(200, 100, 50, 0, Math.PI * 2)
    ctx.strokeStyle = '#374151'
    ctx.lineWidth = 6
    ctx.stroke()

    // Score arc
    ctx.beginPath()
    ctx.arc(200, 100, 50, -Math.PI / 2, -Math.PI / 2 + (score / 100) * Math.PI * 2)
    ctx.strokeStyle = scoreColor
    ctx.lineWidth = 6
    ctx.lineCap = 'round'
    ctx.stroke()

    // Score text
    ctx.font = 'bold 28px Inter, system-ui'
    ctx.fillStyle = scoreColor
    ctx.textAlign = 'center'
    ctx.fillText(Math.round(score).toString(), 200, 108)

    // Label
    ctx.font = '14px Inter, system-ui'
    ctx.fillStyle = '#9ca3af'
    ctx.fillText(scoreLabel, 200, 170)

    // Surah info
    ctx.font = 'bold 16px Inter, system-ui'
    ctx.fillStyle = '#ffffff'
    ctx.fillText(`${surahName} (${surah}:${ayah})`, 200, 200)

    // Stats row
    ctx.font = '12px Inter, system-ui'
    ctx.fillStyle = '#9ca3af'
    if (streak > 0) {
      ctx.fillText(`🔥 ${streak} day streak  •  ${findings} finding${findings !== 1 ? 's' : ''}`, 200, 225)
    }

    // Branding
    ctx.font = 'bold 11px Inter, system-ui'
    ctx.fillStyle = '#10b981'
    ctx.fillText('Tajweed Tutor — AI Qur\'an Coach', 200, 260)

    // Download
    const url = canvas.toDataURL('image/png')
    const link = document.createElement('a')
    link.download = `tajweed-score-${surah}-${ayah}.png`
    link.href = url
    link.click()
  }, [score, scoreColor, scoreLabel, surah, ayah, surahName, streak, findings])

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[160] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 max-w-md w-full p-6 shadow-2xl"
          >
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Share Your Score</h2>

            {/* Preview card */}
            <div
              ref={cardRef}
              className="rounded-xl overflow-hidden mb-4"
              style={{
                background: 'linear-gradient(135deg, #064e3b, #0f172a)',
                padding: '24px',
              }}
            >
              <div className="text-center">
                <div className="relative w-24 h-24 mx-auto mb-3">
                  <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
                    <circle cx="60" cy="60" r="50" fill="none" stroke="#374151" strokeWidth="6" />
                    <circle
                      cx="60" cy="60" r="50" fill="none"
                      stroke={scoreColor} strokeWidth="6" strokeLinecap="round"
                      strokeDasharray={314}
                      strokeDashoffset={314 - (score / 100) * 314}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-bold" style={{ color: scoreColor }}>{Math.round(score)}</span>
                  </div>
                </div>
                <p className="text-gray-400 text-sm mb-1">{scoreLabel}</p>
                <p className="text-white font-semibold">{surahName} ({surah}:{ayah})</p>
                {streak > 0 && (
                  <p className="text-gray-500 text-xs mt-2">🔥 {streak} day streak</p>
                )}
                <p className="text-emerald-500 text-xs font-semibold mt-3">Tajweed Tutor</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <button onClick={handleCopy} className="btn-secondary flex-1 text-sm py-2">
                Copy Text
              </button>
              <button onClick={handleDownload} className="btn-primary flex-1 text-sm py-2">
                Download Image
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default ShareCard
